import {
  Box,
  Text,
  TextLink,
  Skeleton,
  Stack,
  Badge,
  Notice,
  Hidden
} from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { useFormikContext, FormikContextType } from 'formik'
import config from '@/config'
import useGetPromotions from '@/hooks/use-promotions'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import CheckboxWithOptions from './checkbox-with-options'
import { SYNDICATION_PLINK } from './content'

const { IMAGES_URL } = config
type Props = {
  setHasAddons: (a: boolean) => void
}

type Product = {
  billing_period: string
  currency: string
  item_name: string
  metadata: {
    platform: string
  }
  model_name: string
  name: string
  period_end_date: string
  period_start_date: string
  priceId: string
  quantity: number
  type: string
  value: string
}
type PromotionData = {
  products: {
    items: Product[]
  }
  addons: Product[]
}

const AddonStep = ({ setHasAddons }: Props) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const state = 'BC'
  const { data: productData, isLoading }: { data: PromotionData; isLoading: boolean; error: any } =
    useGetPromotions({
      country: 'CA',
      province: state ?? 'BC',
      promotionId: SYNDICATION_PLINK,
      addon: true
    })

  const { values, errors, touched }: FormikContextType<{ addons: string[] }> = useFormikContext()
  const products = Object.values(productData?.addons) as Product[]

  return (
    <Box paddingY="gutter" style={{ width: isMobile ? '100%' : '660px' }}>
      <Box paddingBottom="gutter">
        <Stack space="xsmall">
          <Hidden above="mobile">
            <Stack space="medium">
              <Badge tone="promote">{t('l.usage.credits.recommended.text', 'Recommended')}</Badge>
              <Text size="medium" weight="strong">
                {t(
                  'l.usage.credits.track_your_ads_views.text',
                  `Track your ad’s views, clicks and inquiries`
                )}
              </Text>
              <Box>
                <Text size="medium" weight="regular">
                  {t(
                    'l.usage.credits.unlock_a_monthly_report.text',
                    `Unlock a monthly report covering up to 100 listings for an additional $50 per month.`
                  )}
                </Text>
                <TextLink
                  href={`${IMAGES_URL}/extras/liv.rent_Analytics_Report_Example.pdf`}
                  target="_blank"
                >
                  View example
                </TextLink>
              </Box>
            </Stack>
          </Hidden>
          <Hidden below="tablet">
            <Stack space="xsmall">
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="spaceBetween"
                width="full"
              >
                <Text size="medium" weight="strong">
                  {t(
                    'l.usage.credits.track_your_ads_views.text',
                    `Track your ad’s views, clicks and inquiries`
                  )}
                </Text>
                <Badge tone="promote">{t('l.usage.credits.recommended.text', 'Recommended')}</Badge>
              </Box>
              <Box style={{ maxWidth: '550px' }}>
                <Text size="medium" weight="regular">
                  {t(
                    'l.usage.credits.unlock_a_monthly_report.text',
                    `Unlock a monthly report covering up to 100 listings for an additional $50 per month.`
                  )}{' '}
                  <TextLink
                    href={`${IMAGES_URL}/extras/liv.rent_Analytics_Report_Example.pdf`}
                    target="_blank"
                  >
                    View example
                  </TextLink>
                </Text>
              </Box>
            </Stack>
          </Hidden>
        </Stack>
      </Box>
      {isLoading ? (
        <Skeleton.Shape height={58} width={'100%'} />
      ) : (
        <Stack space="small">
          <Stack space="small">
            <CheckboxWithOptions
              id="analytics"
              name="addons"
              label={t('l.usage_credits.analytics_report.text', 'Analytics Report')}
              dropDownLabel={t('l.usage_credits.duration.dropdown_label', 'Duration')}
              products={products}
              onChange={setHasAddons}
            />
          </Stack>
          {errors?.addons && (
            <Notice tone="critical">
              <Text>{errors.addons}</Text>
            </Notice>
          )}
          {values?.addons?.length > 0 && touched?.addons && (
            <Notice tone="info">
              <Text>
                {t(
                  'l.usage_creits.once_purchased.text',
                  'Once purchased, our team will contact you to discuss next steps.'
                )}
              </Text>
            </Notice>
          )}
        </Stack>
      )}
    </Box>
  )
}

export default AddonStep
